import type { RcFile } from 'antd/es/upload';
import type { ControlGroupAnswers, EarthingMeasurementsByNode } from '../services/controlTaskService';
import type { RcdMeasurementsByNode } from '../types/rcdMeasurement';

export type PanelControlDraft = {
  version: 1;
  updatedAt: string | null;
  reportFormatId: number | null;
  measurements: EarthingMeasurementsByNode;
  rcdMeasurements: RcdMeasurementsByNode;
  answers: ControlGroupAnswers;
  yearValues: Record<string, string>;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

export const readPanelControlDraft = (
  key: string, updatedAt?: string, reportFormatId?: number | null,
): PanelControlDraft | undefined => {
  try {
    const value: unknown = JSON.parse(sessionStorage.getItem(key) ?? 'null');
    if (!isRecord(value) || value.version !== 1 || value.updatedAt !== (updatedAt ?? null)
      || value.reportFormatId !== (reportFormatId ?? null)
      || !isRecord(value.measurements) || !Object.values(value.measurements).every((rows) => Array.isArray(rows) && rows.every(isRecord))
      || !isRecord(value.rcdMeasurements) || !Object.values(value.rcdMeasurements).every(Array.isArray)
      || !isRecord(value.answers) || !Object.values(value.answers).every(isRecord)
      || !isRecord(value.yearValues) || !Object.values(value.yearValues).every((v) => typeof v === 'string')) return undefined;
    return value as PanelControlDraft;
  } catch {
    return undefined;
  }
};

/** Selected images already carry data URLs in draft answers; rebuild pending upload files on reload. */
export const restorePanelDraftImages = (answers: ControlGroupAnswers): Record<string, RcFile> => {
  const files: Record<string, RcFile> = {};
  Object.entries(answers).forEach(([groupId, questions]) => {
    Object.entries(questions).forEach(([questionId, answer]) => {
      (Array.isArray(answer) ? answer : [answer]).forEach((selection: unknown) => {
        if (!isRecord(selection) || selection.id == null) return;
        const images = Array.isArray(selection.images) ? [...selection.images, selection] : [selection];
        images.forEach((image: unknown) => {
          if (!isRecord(image) || image.documentId || typeof image.dataUrl !== 'string') return;
          const match = /^data:([^;]+);base64,(.*)$/.exec(image.dataUrl);
          if (!match) return;
          try {
            const bytes = Uint8Array.from(atob(match[2]), (char) => char.charCodeAt(0));
            const key = `panel:${groupId}:${questionId}:${selection.id}:${image === selection ? 'legacy' : image.key}`;
            files[key] = Object.assign(new File([bytes], String(image.imageName ?? 'pano-gorseli'), { type: match[1] }), { uid: key, lastModifiedDate: new Date() });
          } catch {
            // Malformed local image data is ignored; other draft answers remain recoverable.
          }
        });
      });
    });
  });
  return files;
};
